import React, { FC, ReactNode, useId } from 'react';
import { LinkProps } from 'react-router-dom';

import { ComposeNodeType } from '../../containers/compose/props';
import { TopicBasicFragment } from '../../generated/graphql';
import useIsMobile from '../../hooks/useIsMobile';
import translate from '../../i18n/translate';
import ComposeModalTopic from '../Compose/ComposeModal/ComposeModalTopic';
import TopicTemplateSelection from '../MUI/Compose/TopicTemplateSelection';
import MetaContainerPopup from '../MetaContainerPopup';

type Props = {
  className?: string;
  dataTopicAll: TopicBasicFragment[];
  dataTopicCurrent: TopicBasicFragment['id'] | null;
  dataTopicSaved: TopicBasicFragment['id'] | null;
  dataSearchValue: string;
  inputCustom?: ReactNode;
  isActive: boolean;
  isLoading: boolean;
  linkToggleProps: LinkProps;
  nodeType: ComposeNodeType;
  onSearch: (term: string) => void;
  requestTopicChange: (topicId: TopicBasicFragment['id']) => void;
  requestTopicClose?: () => void;
  shouldValidate?: boolean;
};

/**
 * Topic popup for the compose block
 *
 * @param props                    Props passed to the component
 * @param props.className          styled-components generated class name, needed for styling
 * @param props.dataTopicAll       All available topics
 * @param props.dataTopicCurrent   Currently selected topic
 * @param props.dataTopicSaved     Currently saved topic
 * @param props.dataSearchValue    <input type="search" /> value attribute
 * @param props.inputCustom        Component to use for overview in mobile view
 * @param props.isActive           Whether the component should be visible
 * @param props.isLoading          Whether the data is still being loaded
 * @param props.linkToggleProps    Props for the toggle link
 * @param props.nodeType           Type of the compose form
 * @param props.onSearch           Callback to invoke when search term is changed
 * @param props.requestTopicChange Request that selected topic is changed
 * @param props.requestTopicClose  Request to close topic popup
 * @param props.shouldValidate     Whether the input should be validated
 * @returns                        The component itself
 */
const MetaTopic: FC<Props> = ({
  className,
  dataSearchValue,
  dataTopicAll,
  dataTopicCurrent,
  dataTopicSaved,
  inputCustom,
  isActive,
  isLoading,
  linkToggleProps,
  nodeType,
  onSearch,
  requestTopicChange,
  requestTopicClose,
  shouldValidate,
}) => {
  const isMobile = useIsMobile();

  /**
   * We need to check if currently selected topic is equal to draft/saved topic
   * so we can disable save button if they are equal.
   */
  const isSaveDisabled = dataTopicCurrent === dataTopicSaved;

  const idForm = useId();
  const idHeading = useId();

  // Popup top buttons' labels
  const labelConfirm = translate('GENERAL__SAVE');
  const labelClose = translate('GENERAL__CLOSE');

  const textPlaceholder = translate('COMPOSE__PLACEHOLDER__TOPIC');
  const textHeading = textPlaceholder;

  const topicTitle =
    dataTopicAll.find(topic => topic.id === dataTopicSaved)?.title ?? '';

  if (!isMobile) {
    return (
      <ComposeModalTopic
        ariaDescribedBy={idHeading}
        ariaLabelledBy={idHeading}
        dataSearchValue={dataSearchValue}
        dataTopicAll={dataTopicAll}
        dataTopicSaved={dataTopicSaved}
        isLoading={isLoading}
        onSearch={onSearch}
        requestTopicChange={requestTopicChange}
        shouldAutoFocus={nodeType === 'TEMPLATE'}
        shouldValidate={shouldValidate}
        topicTitle={topicTitle}
      />
    );
  }

  const children = (
    <TopicTemplateSelection
      ariaDescribedBy={idHeading}
      ariaLabelledBy={idHeading}
      dataAll={dataTopicAll}
      dataCurrent={dataTopicSaved}
      dataSearchValue={dataSearchValue}
      isLoading={isLoading}
      onSearch={onSearch}
      requestDataSelect={requestTopicChange}
      type="topic"
    />
  );

  return (
    <MetaContainerPopup
      className={className}
      idForm={idForm}
      idHeading={idHeading}
      inputCustom={inputCustom}
      isSaveDisabled={isSaveDisabled}
      labelClose={labelClose}
      labelConfirm={labelConfirm}
      linkProps={linkToggleProps}
      onClose={requestTopicClose}
      placeholder={`${textPlaceholder} *`}
      shouldHideSave={true}
      shouldShowPopup={isActive}
      textHeading={textHeading}
      value={topicTitle}
    >
      {children}
    </MetaContainerPopup>
  );
};

export default MetaTopic;
